import type { GlobeInstance } from "globe.gl";
import { emitArc } from "./globe/emitArc";
import { initialiseGlobe } from "./globe/globe";
import { getOwnCoordinates } from "./httpServices/coordinates";
import { getDistanceBetween } from "./httpServices/distance";
import { ttsService } from "./tts-service";

// Type declaration for embedded autoscroll manager
declare global {
  interface Window {
    autoscrollManager?: {
      onArabicGrowthCommit: (growthPx: number, isNewLineStart: boolean) => void;
      onEnglishFinalCommit: () => void;
      setAtLive: (next: boolean) => void;
      isContainerNearBottom: () => boolean;
      enterReadingModeForEl?: (el: HTMLElement, durationMs: number) => void;
      exitReadingMode?: () => void;
    };
  }
}

// Lightweight logger available module-wide (fixes scoping for calls below)
const LOG_ENABLED = true; // set to false to silence
function LOG(tag: string, data?: unknown) {
  try {
    if (!LOG_ENABLED) return;
    if (data !== undefined) console.log(`[Baian:${tag}]`, data);
    else console.log(`[Baian:${tag}]`);
  } catch {}
}

/* ============================================================
   UI types & globals
============================================================ */
type UI = {
  slugInput: HTMLInputElement;
  resolveBtn: HTMLButtonElement;

  sessionInput: HTMLInputElement;
  lineInput: HTMLInputElement;
  connectBtn: HTMLButtonElement;
  disconnectBtn: HTMLButtonElement;
  sendBtn: HTMLButtonElement;
  endBtn: HTMLButtonElement;
  statusEl: HTMLElement;
  logEl: HTMLElement;
};

type MsgType = "ai" | "quran" | "hadith";

type TranscriptPair = {
  id: number;
  order: number; // arrival order (future use)
  ar?: string;
  // Keep English for backward compatibility with existing flows (e.g., TTS)
  en?: string;
  // Store all non-Arabic translations keyed by language code (e.g., 'en','ur',...)
  translations?: Record<string, string>;
  arRev?: number;
  isFinalAr?: boolean;
  type?: MsgType; // inferred from tags
  citation?: string; // e.g., "Qur'an 46:35" or "Hadith <ref>"
};

let es: EventSource | null = null;

// Per-pair DOM elements
const pairElements = new Map<
  number,
  {
    block: HTMLElement;
    badge: HTMLElement;
    arEl: HTMLElement;
    enEl: HTMLElement;
    citeEl: HTMLElement;
  }
>();
const transcriptPairs = new Map<number, TranscriptPair>();
const sortedPairIds: number[] = [];
let arrivalCounter = 0;
// Track which transcript pairs we've enqueued for TTS playback per language
const spokenKeys = new Set<string>(); // `${lang}:${pairId}`
// Track which languages have at least one audio ready
const langsWithAudio = new Set<string>();
let preRollInProgress = false;
const postPreRollPairs: TranscriptPair[] = [];
let joinAnchorPairId: number | null = null;
// Track the active session for manifest-driven backfill
let currentManifestSessionId: string | null = null;

// Autoscroll helpers (now managed by embedded script in HTML)
let lastArabicPairIdStarted = 0;

// Optional debounce for chatty partials
const arUpdateTimers = new Map<number, number>();

/* ============================================================
   Boot
============================================================ */
window.addEventListener("DOMContentLoaded", () => {
  const flags = new URL(location.href).searchParams;
  const debug = flags.get("debug") === "1";
  const showGlobe = flags.get("globe") === "1";

  // Lightweight logger for diagnostics
  const LOG_ENABLED = true; // set to false to silence
  function LOG(tag: string, data?: unknown) {
    try {
      if (!LOG_ENABLED) return;
      if (data !== undefined) console.log(`[Baian:${tag}]`, data);
      else console.log(`[Baian:${tag}]`);
    } catch {}
  }

  // Debug panel visibility
  const debugPanel = document.getElementById("sse-panel") as HTMLElement | null;
  if (debugPanel) debugPanel.style.display = debug ? "block" : "none";

  // Globe demo
  if (showGlobe) {
    void startGlobeDemo();
  } else {
    // hide optional globe bits
    const captionEl = document.querySelector(".caption") as HTMLElement | null;
    const globeContainerEl = document.querySelector(
      ".globe-container"
    ) as HTMLElement | null;
    captionEl && (captionEl.style.display = "none");
    globeContainerEl && (globeContainerEl.style.display = "none");
  }

  // Route detection: history list/detail vs live
  const route = detectRoute();

  // History flows
  if (route.mode === "history-list") {
    initHistoryList(route.slug);
    wireHistoryButton(route.slug);
    return;
  }
  if (route.mode === "history-detail") {
    initHistoryDetail(route.slug, route.sessionId);
    wireHistoryButton(route.slug);
    return;
  }

  // Main live UI (default)
  initLiveUI();
  // Wire History button on live route too
  try {
    const slug = getSlugFromPath() || "";
    wireHistoryButton(slug);
  } catch {}
  LOG("boot:initLiveUI");

  // Debug hooks
  if (debug) initSSEUI();
  LOG("boot:initSSEUI", { debug });

  // Initialize audio controls
  initAudioControls();
  LOG("boot:initAudioControls");

  // Initialize TTS controls
  initTTSControls();
  LOG("boot:initTTSControls");

  // Initialize language controls
  initLanguageControls();
  LOG("boot:initLanguageControls");

  // Global first-gesture unlock safeguard (mobile safari/samsung)
  try {
    window.addEventListener(
      "pointerup",
      () => {
        void ttsService.unlockAudio();
      },
      { once: true, passive: true }
    );
  } catch {}

  // Page is ready → hide loader
  document.querySelector(".loading-placeholder")?.classList.remove("loading");
  document.querySelector(".content")?.classList.remove("loading");
});

/* ============================================================
   History routing + UI
============================================================ */
function detectRoute():
  | { mode: "live" }
  | { mode: "history-list"; slug: string }
  | { mode: "history-detail"; slug: string; sessionId: string } {
  try {
    const path = decodeURIComponent(location.pathname);
    // /S/{session_id} alias (optional support)
    const mS = path.match(/^\/S\/([^\/]+)\/?$/);
    if (mS) {
      // no slug available; fall back to live if needed
      return {
        mode: "history-detail",
        slug: getSlugFromPath() || "",
        sessionId: mS[1],
      };
    }
    const m = path.match(/^\/[mM]\/([^\/]+)(?:\/(.*))?$/);
    if (!m) return { mode: "live" };
    const slug = m[1];
    const rest = m[2] || "";
    if (rest === "history" || rest === "history/")
      return { mode: "history-list", slug };
    if (rest && rest !== "history/")
      return {
        mode: "history-detail",
        slug,
        sessionId: rest.replace(/\/$/, ""),
      };
    return { mode: "live" };
  } catch {
    return { mode: "live" };
  }
}

function wireHistoryButton(slug: string) {
  try {
    const btn = document.getElementById("btn-history");
    btn?.addEventListener("click", () => {
      location.href = `/M/${encodeURIComponent(slug)}/history`;
    });
  } catch {}
}

async function initHistoryList(slug: string) {
  // Reuse transcript container for simple list rendering
  const transcriptEl = document.getElementById(
    "transcript"
  ) as HTMLElement | null;
  const listEl = document.getElementById(
    "transcript-list"
  ) as HTMLElement | null;
  if (!transcriptEl || !listEl) return;
  transcriptEl.style.display = "block";
  listEl.innerHTML = "";

  let cursor: string | null = null;
  const limit = 20;

  const load = async () => {
    const u = new URL(`/api/history/sessions`, location.origin);
    u.searchParams.set("slug", slug);
    u.searchParams.set("limit", String(limit));
    if (cursor) u.searchParams.set("cursor", cursor);
    const resp = await fetch(u.toString(), { method: "GET" });
    if (!resp.ok) return;
    const data: any = await resp.json().catch(() => ({}));
    const items: Array<{ session_id: string }> = data?.items || [];
    for (const it of items) {
      const a = document.createElement("a");
      a.href = `/M/${encodeURIComponent(slug)}/${encodeURIComponent(
        it.session_id
      )}`;
      a.textContent = `Session ${it.session_id}`;
      a.style.display = "block";
      a.style.margin = "8px 0";
      listEl.appendChild(a);
    }
    cursor = data?.next_cursor ?? null;
    if (!listEl.childElementCount) {
      const p = document.createElement("p");
      p.textContent = "No past khutbahs yet.";
      p.style.opacity = "0.7";
      listEl.appendChild(p);
    }
    renderLoadMore(listEl, !!cursor, async () => {
      await load();
    });
  };
  await load();
}

function renderLoadMore(
  root: HTMLElement,
  hasMore: boolean,
  onMore: () => void
) {
  // remove existing button
  const prev = root.querySelector("button[data-role='load-more']");
  if (prev) prev.remove();
  if (!hasMore) return;
  const btn = document.createElement("button");
  btn.setAttribute("data-role", "load-more");
  btn.textContent = "Load more";
  btn.className = "chip";
  btn.style.margin = "12px 0";
  btn.addEventListener("click", onMore);
  root.appendChild(btn);
}

async function initHistoryDetail(slug: string, sessionId: string) {
  const transcriptEl = document.getElementById(
    "transcript"
  ) as HTMLElement | null;
  const listEl = document.getElementById(
    "transcript-list"
  ) as HTMLElement | null;
  if (!transcriptEl || !listEl) return;
  transcriptEl.style.display = "block";
  listEl.innerHTML = "";

  let cursor: number | null = null;
  let ended = false;

  const renderChunk = (items: Array<{ pair_id: number; text: string }>) => {
    for (const it of items) {
      const block = document.createElement("div");
      block.className = "msg msg-en";
      block.textContent = it.text || "";
      block.style.margin = "8px 0";
      listEl.appendChild(block);
    }
  };

  const load = async (limit: number) => {
    const u = new URL(`/api/history/pairs`, location.origin);
    u.searchParams.set("session_id", sessionId);
    u.searchParams.set("lang", "en");
    u.searchParams.set("limit", String(limit));
    if (cursor != null) u.searchParams.set("cursor", String(cursor));
    const resp = await fetch(u.toString(), { method: "GET" });
    if (!resp.ok) return;
    const data: any = await resp.json().catch(() => ({}));
    const items: Array<{ pair_id: number; text: string }> = data?.items || [];
    renderChunk(items);
    if (items.length) cursor = items[items.length - 1].pair_id;
    const next = data?.next_cursor ?? null;
    ended = !!(data && data.ended_at);
    renderLoadMore(listEl, !!next, async () => {
      await load(200);
    });
    return { nextExists: !!next };
  };

  await load(300);

  // Light auto-refresh while ongoing
  const REFRESH_MS = 12000;
  if (!ended) {
    const timer = setInterval(async () => {
      if (ended) {
        clearInterval(timer);
        return;
      }
      const before = cursor;
      await load(200);
      if (ended) clearInterval(timer);
      // if no progress and not ended, keep polling
      if (cursor === before && ended) clearInterval(timer);
    }, REFRESH_MS);
  }
}

/* ============================================================
   Live UI wiring
============================================================ */
function initLiveUI() {
  const slug = getSlugFromPath();
  const slugLabel = document.getElementById("slug-label") as HTMLElement | null;
  const statusPill = document.getElementById(
    "status-pill"
  ) as HTMLElement | null;
  const transcriptEl = document.getElementById(
    "transcript"
  ) as HTMLElement | null;
  // autoscroll manager is now initialized by embedded script in HTML
  const phoneTitle = document.getElementById(
    "phone-title"
  ) as HTMLElement | null;
  // (reverted) no arabic island wiring

  if (slugLabel) slugLabel.textContent = slug ? `live: ${slug}` : "live";
  if (phoneTitle && slug) phoneTitle.textContent = `${slug} – Live`;

  // Font +/- controls (phone content scale)
  const btnInc = document.getElementById("btn-font-inc");
  const btnDec = document.getElementById("btn-font-dec");
  // Apply saved transcript font scale on load
  try {
    const saved = (
      typeof localStorage !== "undefined"
        ? localStorage.getItem("global_font_size")
        : null
    ) as string | null;
    const initial = saved ? Math.max(0.8, Math.min(2.5, parseFloat(saved))) : 1;
    document.documentElement.style.setProperty(
      "--global-font-size",
      String(initial)
    );
  } catch {}
  btnInc?.addEventListener("click", () => adjustGlobalFontScale(+0.1));
  btnDec?.addEventListener("click", () => adjustGlobalFontScale(-0.1));

  // Globe toggle: jump to same URL with/without ?globe=1
  const btnGlobe = document.getElementById("btn-globe");
  btnGlobe?.addEventListener("click", () => {
    const url = new URL(location.href);
    const on = url.searchParams.get("globe") === "1";
    if (on) url.searchParams.delete("globe");
    else url.searchParams.set("globe", "1");
    location.href = url.toString();
  });

  // If no slug, keep idle
  if (!slug) {
    setStatusPill(statusPill, "not live", "#999", "#eee");
    return;
  }

  setStatusPill(statusPill, "checking…", "#333", "#eee");

  // Presence-driven flow: subscribe to /api/presence and (re)attach transcript SSE on demand
  const presenceUrl = `/api/presence?slug=${encodeURIComponent(slug)}`;
  const pes = new EventSource(presenceUrl);
  try {
    LOG("presence:connect", { url: presenceUrl });
    // Light instrumentation to confirm connection lifecycle
    (pes as any).onopen = () => {
      LOG("presence:onopen");
      // One-shot KV fallback: if the DO doesn't emit a named 'state', query /api/live
      (async () => {
        try {
          const res = await fetch(
            `/api/live?slug=${encodeURIComponent(slug)}`,
            {
              headers: { accept: "application/json" },
              cache: "no-store",
            }
          );
          if (res.ok) {
            const d = await res.json().catch(() => ({} as any));
            const sid = d?.session_id ? String(d.session_id) : "";
            if (sid) attachSession(sid);
          }
        } catch {}
      })();
    };
    (pes as any).onmessage = (ev: MessageEvent) => {
      // Parse default messages as state snapshots if shape matches
      const txt = (ev?.data || "").trim();
      LOG("presence:message", { len: txt.length });
      try {
        const s = JSON.parse(txt);
        if (s && typeof s.live === "boolean") {
          handlePresenceState({
            live: !!s.live,
            session_id: s.session_id ? String(s.session_id) : null,
          });
        }
      } catch {}
    };
  } catch {}
  let currentSessionId: string | null = null;
  let currentStream: EventSource | null = null;

  const handlePresenceState = (s: {
    live: boolean;
    session_id: string | null;
  }) => {
    try {
      LOG("presence:state", {
        live: s.live,
        session_id: s.session_id,
        hasStream: !!currentStream,
        currentSessionId,
      });
    } catch {}
    if (s.live && s.session_id) {
      attachSession(s.session_id);
    } else {
      // Revert to r2 behavior: explicitly close any existing session stream on not-live
      setStatusPill(statusPill, "not live", "#999", "#eee");
      if (currentStream) {
        try {
          currentStream.close();
        } catch {}
        currentStream = null;
      }
      currentSessionId = null;
    }
  };

  const attachSession = (sid: string) => {
    LOG("presence:attachSession", { previous: currentSessionId, next: sid });
    if (currentSessionId === sid) return;
    // close previous
    if (currentStream) {
      try {
        currentStream.close();
      } catch {}
      currentStream = null;
    }
    // hard session boundary: clear UI/state before attaching new stream
    resetUIForNewSession();
    // new session: reset TTS dedupe so last-3 can replay for the new session
    try {
      spokenKeys.clear();
      langsWithAudio.clear();
    } catch {}
    currentSessionId = sid;
    try {
      currentManifestSessionId = sid;
    } catch {}
    currentStream = subscribeToSession(sid, {
      onOpen: () => {
        LOG("session:onOpen", { sessionId: sid });
        setStatusPill(statusPill, "live", "#0b421a", "#b9f6ca");
      },
      onLine: (payload: any) => {
        try {
          const container = document.getElementById(
            "transcript"
          ) as HTMLElement | null;
          const visible = !!container && container.style.display !== "none";
          LOG("session:onLine", {
            sessionId: sid,
            kind: payload?.kind,
            lang: payload?.lang,
            pair_id: payload?.pair_id,
            text_len: String(payload?.text || "").length,
            selectedLanguage,
            transcriptVisible: visible,
          });
        } catch {}
        const list = document.getElementById("transcript-list") as HTMLElement;
        const container = document.getElementById(
          "transcript"
        ) as HTMLElement | null;
        const before = container ? container.scrollHeight : 0;
        renderIncomingLine(payload, list);
        try {
          const after = container ? container.scrollHeight : 0;
          LOG("session:rendered", {
            pair_id: payload?.pair_id,
            growth: after - before,
          });
        } catch {}
        const after = container ? container.scrollHeight : before;
        const growth = Math.max(0, after - before);
        const tEl = document.getElementById(
          "translating"
        ) as HTMLElement | null;
        if (tEl) {
          const pid = Number(payload?.pair_id) || 0;
          if (payload?.kind === "ar") {
            // when a new AR id begins, we're transcribing
            tEl.querySelector(".translating-text")?.replaceChildren();
            (
              tEl.querySelector(".translating-text") as HTMLElement | null
            )?.append("Transcribing…");
            tEl.classList.add("show");
          } else if (payload?.kind === "en") {
            // when EN for that AR arrives, we're translating (briefly)
            tEl.querySelector(".translating-text")?.replaceChildren();
            (
              tEl.querySelector(".translating-text") as HTMLElement | null
            )?.append("Translating…");
            // keep visible for a short moment then hide
            window.setTimeout(() => tEl.classList.remove("show"), 600);
          }
        }
        // Autoscroll triggers using embedded manager
        try {
          const kind = String(payload?.kind || "");
          const pid = Number(payload?.pair_id) || 0;
          if (kind === "ar") {
            // new-line tick if pair id increases
            let isNew = false;
            if (pid > lastArabicPairIdStarted) {
              lastArabicPairIdStarted = pid;
              isNew = true;
            }
            // Use embedded autoscroll manager
            if (window.autoscrollManager) {
              window.autoscrollManager.onArabicGrowthCommit(growth, isNew);
            }
          } else if (kind === "en") {
            // Use embedded autoscroll manager
            if (window.autoscrollManager) {
              window.autoscrollManager.onEnglishFinalCommit();
            }
          }
        } catch {}
      },
      onEnd: () => {
        setStatusPill(statusPill, "ended", "#6b4b00", "#ffe0b2");
        currentStream = null;
        currentSessionId = null;
      },
      onError: () =>
        setStatusPill(statusPill, "reconnecting…", "#fff", "#b00020"),
    });
  };

  // Presence snapshot updates
  pes.addEventListener("state", (ev: MessageEvent) => {
    try {
      const s = JSON.parse(ev.data) as {
        live: boolean;
        session_id: string | null;
      };
      handlePresenceState(s);
    } catch {
      /* ignore bad JSON */
    }
  });
  // Explicit start/end (optional redundancy)
  pes.addEventListener("start", (ev: MessageEvent) => {
    try {
      const { session_id } = JSON.parse(ev.data) as { session_id: string };
      if (session_id) {
        // treat explicit start as a hard boundary even if SID is reused
        if (currentStream) {
          try {
            currentStream.close();
          } catch {}
          currentStream = null;
        }
        currentSessionId = null;
        resetUIForNewSession();
        attachSession(session_id);
      }
    } catch {}
  });
  pes.addEventListener("end", () => {
    setStatusPill(statusPill, "ended", "#6b4b00", "#ffe0b2");
    if (currentStream) {
      try {
        currentStream.close();
      } catch {}
      currentStream = null;
    }
    currentSessionId = null;
  });
  pes.onerror = () => {
    // Presence transport issue (will auto-retry)
    setStatusPill(statusPill, "listening…", "#333", "#eee");
  };
}

function setStatusPill(
  el: HTMLElement | null,
  text: string,
  fg: string,
  bg: string
) {
  if (!el) return;
  el.textContent = text;
  el.style.color = fg;
  el.style.background = bg;
}

function adjustGlobalFontScale(delta: number) {
  const cs = getComputedStyle(document.documentElement);
  const cur = parseFloat(cs.getPropertyValue("--global-font-size") || "1") || 1;
  const next = Math.max(0.8, Math.min(2.5, cur + delta));
  document.documentElement.style.setProperty(
    "--global-font-size",
    String(next)
  );
  try {
    if (typeof localStorage !== "undefined")
      localStorage.setItem("global_font_size", String(next));
  } catch {}
}

/* ============================================================
   Session boundary reset (UI + state)
============================================================ */
function resetUIForNewSession() {
  try {
    const list = document.getElementById("transcript-list");
    if (list) list.innerHTML = "";
  } catch {}
  try {
    const feed = document.getElementById("feed-list");
    if (feed) feed.innerHTML = "";
  } catch {}

  try {
    for (const t of Array.from(arUpdateTimers.values())) {
      window.clearTimeout(t as unknown as number);
    }
    arUpdateTimers.clear();
  } catch {}

  try {
    pairElements.clear();
    transcriptPairs.clear();
    sortedPairIds.length = 0;
    arrivalCounter = 0;
    lastArabicPairIdStarted = 0;
    joinAnchorPairId = null;
    spokenKeys.clear();
    langsWithAudio.clear();
  } catch {}

  try {
    const tEl = document.getElementById("translating");
    if (tEl) tEl.classList.remove("show");
  } catch {}

  try {
    if (window.autoscrollManager) {
      window.autoscrollManager.setAtLive(true);
    }
  } catch {}

  try {
    ttsService.cleanup();
  } catch {}
}

/* ============================================================
   SSE: connect & render
============================================================ */
function subscribeToSession(
  sessionId: string,
  handlers: {
    onOpen?: () => void;
    onLine?: (payload: any, eventId?: string) => void;
    onEnd?: (body?: string) => void;
    onError?: () => void;
  }
): EventSource {
  const endpoint = `/api/subscribe?session_id=${encodeURIComponent(sessionId)}`;
  try {
    (window as any).__lastSubscribeUrl = endpoint;
    LOG("session:subscribe:init", { endpoint });
  } catch {}
  const stream = new EventSource(endpoint);

  stream.onopen = () => {
    try {
      LOG("session:subscribe:onopen", { endpoint });
    } catch {}
    handlers.onOpen?.();
  };
  stream.onmessage = (e) => {
    try {
      const data = JSON.parse((e as MessageEvent).data || "{}");
      // intercept control-plane TTS notices
      if (data?.type === "tts_ready") {
        try {
          const langEvent = String(data?.lang || "en").toLowerCase();
          if (langEvent) langsWithAudio.add(langEvent);
          updateAudioChipVisibility();
          if (ttsUI.autoTTSEnabled) {
            if (selectedLanguage !== langEvent) return;
            const pid = Number(data?.pair_id);
            const key = `${langEvent}:${pid}`;
            if (!spokenKeys.has(key)) {
              void ttsService.unlockAudio();
              const rawUrl = String(data?.url || "");
              const isAbs = /^https?:\/\//i.test(rawUrl);
              let url = isAbs ? rawUrl : location.origin + rawUrl;
              // Add extra tts/ prefix if URL starts with /tts/ but not /tts/tts/
              if (rawUrl.includes("/tts/") && !rawUrl.includes("/tts/tts/")) {
                if (isAbs) {
                  // For absolute URLs, insert /tts/ after the domain
                  const urlObj = new URL(rawUrl);
                  urlObj.pathname = "/tts" + urlObj.pathname;
                  url = urlObj.toString();
                } else {
                  // For relative URLs, add /tts/ prefix
                  url = location.origin + "/tts" + rawUrl;
                }
              }
              console.log("🎵 TTS URL transformation:", {
                rawUrl,
                finalUrl: url,
              });
              ttsService.enqueueUrl(url);
              if (Number.isFinite(pid)) spokenKeys.add(key);
            }
          }
        } catch {}
        return;
      }
      handlers.onLine?.(data, (e as MessageEvent).lastEventId || undefined);
    } catch {
      // ignore bad JSON
    }
  };
  stream.addEventListener("end", (e: MessageEvent) => {
    LOG("session:end", { data: e.data });
    handlers.onEnd?.(e.data);
    try {
      stream.close();
    } catch {}
  });
  stream.onerror = () => {
    try {
      LOG("session:subscribe:onerror", { endpoint });
    } catch {}
    handlers.onError?.();
  };
  return stream;
}

/** Core render: we expect { pair_id, kind: 'ar'|'en', lang: <iso>, text, partial?, rev? } */
function renderIncomingLine(line: any, listEl: HTMLElement) {
  const pairId = Number(line?.pair_id);
  const kind = String(line?.kind || "");
  const lang = String(line?.lang || "").toLowerCase();
  const rawText = String(line?.text || "");
  try {
    LOG("render:incoming", {
      pairId,
      kind,
      lang,
      text_len: rawText.length,
      selectedLanguage,
    });
  } catch {}
  // Accept Arabic (kind==='ar') or any supported translation language (strictly from line.lang)
  const supported = new Set(supportedLanguages);
  if (!pairId || !rawText) return;
  if (kind !== "ar") {
    // Require a valid explicit language code; do not fall back to kind
    if (!lang || !supported.has(lang)) return;
  }

  const text = normalizeText(rawText);
  const rev = typeof line?.rev === "number" ? (line.rev as number) : undefined;
  const isPartial =
    line?.partial === true ? true : line?.partial === false ? false : undefined;

  let pair = transcriptPairs.get(pairId);
  if (!pair) {
    pair = { id: pairId, order: ++arrivalCounter };
    transcriptPairs.set(pairId, pair);
    // ensure DOM exists in sorted order by pairId
    ensurePairElements(pairId, listEl);
  }
  const els = ensurePairElements(pairId, listEl);

  // Infer message type + extract content/citation
  const parsed = extractTaggedContent(text);
  if (!pair.type && parsed.type) pair.type = parsed.type;
  if (!pair.citation && parsed.citation) pair.citation = parsed.citation;

  // Update badge when type is known
  if (pair.type) setBadge(els.badge, pair.type);

  if (kind === "ar") {
    if (pair.isFinalAr) return; // ignore after final

    // enforce monotonic rev for partials
    if (
      isPartial !== false &&
      pair.arRev != null &&
      (rev == null || rev <= pair.arRev)
    ) {
      return;
    }

    const applyUpdate = () => {
      pair.ar = parsed.content;
      if (rev != null) pair.arRev = rev;
      if (isPartial === false) pair.isFinalAr = true;

      // per-line AR update (classic behavior)
      els.arEl.textContent = pair.ar || "";

      // citation (if Qur'an/Hadith), attach under EN when available else show here
      updateCitation(els.citeEl, pair.citation);
    };

    // debounce chatty partials
    const delay = isPartial === false ? 0 : 140;
    const existingTimer = arUpdateTimers.get(pairId);
    if (existingTimer) {
      window.clearTimeout(existingTimer);
      arUpdateTimers.delete(pairId);
    }
    if (delay > 0) {
      const t = window.setTimeout(() => {
        applyUpdate();
        arUpdateTimers.delete(pairId);
      }, delay);
      arUpdateTimers.set(pairId, t as unknown as number);
    } else {
      applyUpdate();
    }
  } else {
    // Any non-Arabic translation (e.g., en, ur, ms, ...)
    // Per reference.md: kind==='en' for all translations; actual language is in line.lang
    const langCode = lang; // strictly use provided language code
    if (!pair.translations) pair.translations = {};
    pair.translations[langCode] = parsed.content;

    // Maintain legacy .en field for existing flows
    if (langCode === "en") pair.en = parsed.content;

    // Upgrade type/citation based on parsed text if not already set or if scripture
    if (!pair.type || parsed.type === "quran" || parsed.type === "hadith") {
      pair.type = parsed.type;
    }
    if (parsed.citation) {
      pair.citation = parsed.citation;
    }
    if (pair.type) setBadge(els.badge, pair.type);

    // Only reveal/update the visible translation line if this is the currently selected language
    if (selectedLanguage === langCode) {
      // Direction handling: Urdu and other RTL languages
      applyTranslationDirection(els.enEl, langCode);
      els.enEl.textContent = pair.translations[langCode] || "";
      els.enEl.style.display = "";
      // Hide Arabic when TL for selected language is present
      try {
        els.arEl.style.display = "none";
      } catch {}
      try {
        LOG("render:showTL", { pairId, lang: langCode });
      } catch {}
      // Enter reading mode for this TL block
      try {
        const blockEl = els.enEl;
        const words = (pair.translations[langCode] || "")
          .trim()
          .split(/\s+/)
          .filter(Boolean).length;
        const wpm = 180; // base words-per-minute
        const secs = Math.min(
          12,
          Math.max(1.5, (words / Math.max(1, wpm)) * 60 * 1.2)
        );
        const ms = Math.round(secs * 1000);
        if (
          window.autoscrollManager &&
          typeof window.autoscrollManager.enterReadingModeForEl === "function"
        ) {
          window.autoscrollManager.enterReadingModeForEl(blockEl, ms);
        }
      } catch {}
    }

    updateCitation(els.citeEl, pair.citation);
    if (langCode === "en") appendToFeed(pair);

    // Treat any translation final arrival as a commit for autoscroll purposes
    try {
      if (window.autoscrollManager) {
        window.autoscrollManager.onEnglishFinalCommit();
      }
    } catch {}

    // no-op: audio toggle visibility is managed globally per selected language
  }
}

function updateCitation(el: HTMLElement, citation?: string) {
  if (!el) return;
  if (citation) {
    el.style.display = "";
    el.textContent = citation;
  } else {
    el.style.display = "none";
    el.textContent = "";
  }
}

function setBadge(el: HTMLElement, type: MsgType) {
  el.className =
    "badge " +
    (type === "quran"
      ? "badge--quran"
      : type === "hadith"
      ? "badge--hadith"
      : "badge--ai");
  // tag the container for CSS rules (e.g., hide/show per-line AR)
  try {
    const block = el.parentElement as HTMLElement | null;
    if (block) {
      const root = block.parentElement as HTMLElement | null;
      const host = root?.parentElement as HTMLElement | null; // .msg
      if (host) host.classList.toggle("quran", type === "quran");
    }
  } catch {}
  el.textContent =
    type === "quran" ? "Qur'an" : type === "hadith" ? "Hadith" : "AI";
}

/* Ensure a message block exists in sorted order; return handles */
function ensurePairElements(
  pairId: number,
  listEl: HTMLElement
): {
  block: HTMLElement;
  badge: HTMLElement;
  arEl: HTMLElement;
  enEl: HTMLElement;
  citeEl: HTMLElement;
} {
  let els = pairElements.get(pairId);
  if (els) return els;

  // Find insert index (sorted by pairId)
  const insertIndex = findInsertPosition(pairId, sortedPairIds);
  sortedPairIds.splice(insertIndex, 0, pairId);

  // Build block
  const block = document.createElement("div");
  block.className = "msg";
  block.dataset.pairId = String(pairId);

  const badge = document.createElement("div");
  badge.className = "badge badge--ai";
  badge.textContent = "AI";

  const body = document.createElement("div");
  body.className = "msg-body";

  const arEl = document.createElement("div");
  arEl.className = "msg-ar";

  const enEl = document.createElement("div");
  enEl.className = "msg-en";
  enEl.style.display = "none";

  const citeEl = document.createElement("div");
  citeEl.className = "msg-citation";
  citeEl.style.display = "none";

  body.appendChild(arEl);
  body.appendChild(enEl);
  body.appendChild(citeEl);

  block.appendChild(badge);
  block.appendChild(body);

  listEl.insertBefore(block, listEl.children[insertIndex] || null);

  els = { block, badge, arEl, enEl, citeEl };
  pairElements.set(pairId, els);
  return els;
}

function findInsertPosition(id: number, arr: number[]): number {
  let low = 0,
    high = arr.length;
  while (low < high) {
    const mid = (low + high) >> 1;
    if (arr[mid] < id) low = mid + 1;
    else high = mid;
  }
  return low;
}

/* ============================================================
   Parsing: tags + normalization
============================================================ */

/**
 * Extracts special tags and returns clean content for rendering.
 * Supports:
 *   [QURAN ref="46:35"]...[/QURAN]  → type="quran", citation="Qur'an 46:35"
 *   [HADITH ref="..."]...[/HADITH]  → type="hadith", citation="Hadith …"
 * If no tags, type="ai".
 */
function extractTaggedContent(input: string): {
  type: MsgType;
  content: string;
  citation?: string;
} {
  const s = String(input ?? "");

  const quranRefs: string[] = [];
  const hadithRefs: string[] = [];

  const quranRe = /\[QURAN\b(?:\s+ref="([^"]+)")?\]([\s\S]*?)\[\/QURAN\]/gi;
  const hadithRe = /\[HADITH\b(?:\s+ref="([^"]+)")?\]([\s\S]*?)\[\/HADITH\]/gi;

  let replaced = s.replace(quranRe, (_m, ref: string, inner: string) => {
    const r = (ref || "").trim();
    quranRefs.push(r ? `Qur'an ${r}` : "Qur'an");
    return inner;
  });

  replaced = replaced.replace(hadithRe, (_m, ref: string, inner: string) => {
    const r = (ref || "").trim();
    hadithRefs.push(r ? `Hadith ${r}` : "Hadith");
    return inner;
  });

  if (quranRefs.length > 0) {
    return {
      type: "quran",
      content: replaced.trim(),
      citation: quranRefs.join("; "),
    };
  }
  if (hadithRefs.length > 0) {
    return {
      type: "hadith",
      content: replaced.trim(),
      citation: hadithRefs.join("; "),
    };
  }

  // Fallback
  return { type: "ai", content: s.trim() };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function normalizeText(text: string): string {
  try {
    // Tighten spaces before common punctuation (Arabic + Latin)
    return text.replace(/\s+([.!?؟۔،؛…:])/g, "$1");
  } catch {
    return text;
  }
}

/* ============================================================
   Compact "Live feed" (right-hand card)
============================================================ */
const FEED_MAX = 8;
function appendToFeed(pair: TranscriptPair) {
  const list = document.getElementById("feed-list");
  if (!list) return;
  const item = document.createElement("div");
  item.className = "feed-item";

  const badge = document.createElement("div");
  badge.className =
    "feed-badge " +
    (pair.type === "quran"
      ? "feed-badge--quran"
      : pair.type === "hadith"
      ? "feed-badge--hadith"
      : "feed-badge--ai");
  badge.textContent =
    pair.type === "quran" ? "Qur'an" : pair.type === "hadith" ? "Hadith" : "AI";

  const wrap = document.createElement("div");
  const text = document.createElement("div");
  text.className = "feed-text";
  text.textContent = pair.en || pair.ar || "";

  const cite = document.createElement("div");
  cite.className = "feed-cite";
  if (pair.citation) {
    cite.textContent = pair.citation;
  } else {
    cite.style.display = "none";
  }

  wrap.appendChild(text);
  wrap.appendChild(cite);

  item.appendChild(badge);
  item.appendChild(wrap);

  list.appendChild(item);

  // Trim to last FEED_MAX
  while (list.children.length > FEED_MAX) {
    list.removeChild(list.firstElementChild as Element);
  }
}

/* ============================================================
   Debug SSE test panel (unchanged behavior)
============================================================ */
function initSSEUI() {
  const ui: UI = {
    slugInput: q<HTMLInputElement>("#slug"),
    resolveBtn: q<HTMLButtonElement>("#resolve-btn"),

    sessionInput: q<HTMLInputElement>("#session-id"),
    lineInput: q<HTMLInputElement>("#line-input"),
    connectBtn: q<HTMLButtonElement>("#connect-btn"),
    disconnectBtn: q<HTMLButtonElement>("#disconnect-btn"),
    sendBtn: q<HTMLButtonElement>("#send-btn"),
    endBtn: q<HTMLButtonElement>("#end-btn"),
    statusEl: q<HTMLElement>("#status"),
    logEl: q<HTMLElement>("#sse-log"),
  };

  // Seed from URL: ?session_id=… takes precedence; else /m/{slug} or ?slug=…
  const url = new URL(location.href);
  const qpSession = url.searchParams.get("session_id");
  const pathSlug = getSlugFromPath();
  const qpSlug = url.searchParams.get("slug");

  if (pathSlug) ui.slugInput.value = pathSlug;
  else if (qpSlug) ui.slugInput.value = qpSlug;

  if (qpSession) {
    ui.sessionInput.value = qpSession;
    connect(ui);
  } else if (ui.slugInput.value) {
    resolveAndConnect(ui);
  } else {
    ui.sessionInput.value = "demo";
  }

  ui.resolveBtn.addEventListener("click", () => resolveAndConnect(ui));
  ui.connectBtn.addEventListener("click", () => connect(ui));
  ui.disconnectBtn.addEventListener("click", () => disconnect(ui));
  ui.sendBtn.addEventListener("click", () => sendLine(ui));
  ui.endBtn.addEventListener("click", () => endSession(ui));
}

async function resolveAndConnect(ui: UI) {
  const slug = ui.slugInput.value.trim();
  if (!slug) {
    setStatus(ui, "missing slug");
    return;
  }
  setStatus(ui, "resolving…");

  // Prefer POST /api/live-index (write-then-trust) if a session_id is provided.
  // Include Authorization: Bearer <token> if present in localStorage under 'edge_index_bearer'.
  const providedSession = ui.sessionInput.value.trim();
  const bearer = (
    typeof localStorage !== "undefined"
      ? localStorage.getItem("edge_index_bearer")
      : ""
  )?.trim();

  if (providedSession) {
    try {
      append(ui, `📝 POST /api/live-index slug=${slug}`);
      const resPost = await fetch(`/api/live-index`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          ...(bearer ? { authorization: `Bearer ${bearer}` } : {}),
          accept: "application/json",
        },
        body: JSON.stringify({
          slug,
          session_id: providedSession,
          status: "live",
        }),
      });

      if (resPost.ok) {
        const data = await resPost.json().catch(() => ({} as any));
        const sid = data?.session_id || providedSession;
        if (sid) {
          ui.sessionInput.value = String(sid);
          setStatus(ui, "connecting…");
          connect(ui);
          return;
        }
      }
      // If unauthorized or not ok, fall back to GET path below
      append(ui, `↩️ POST fallback (${resPost.status}) → trying GET /api/live`);
    } catch {
      append(ui, `↩️ POST failed → trying GET /api/live`);
    }
  }

  append(ui, `🔎 GET /api/live?slug=${slug}`);

  try {
    const res = await fetch(`/api/live?slug=${encodeURIComponent(slug)}`, {
      headers: { accept: "application/json" },
    });

    if (res.status === 204) {
      setStatus(ui, "not live");
      append(ui, `⏸ not live for slug=${slug}`);
      return;
    }
    if (!res.ok) {
      setStatus(ui, `upstream error ${res.status}`);
      return;
    }

    const data = await res.json().catch(() => ({} as any));
    const sid = data?.session_id;
    if (!sid) {
      setStatus(ui, "no session_id in response");
      return;
    }

    ui.sessionInput.value = String(sid);
    connect(ui);
  } catch {
    setStatus(ui, "live lookup failed");
  }
}

function connect(ui: UI) {
  const sessionId = ui.sessionInput.value.trim();
  if (!sessionId) return setStatus(ui, "invalid session_id");

  disconnect(ui, { silent: true });

  // clear UI/state for a fresh debug connection
  resetUIForNewSession();

  setStatus(ui, "connecting…");
  const endpoint = `/api/subscribe?session_id=${encodeURIComponent(sessionId)}`;
  es = new EventSource(endpoint);
  try {
    currentManifestSessionId = sessionId;
  } catch {}

  es.onopen = () => {
    setStatus(ui, "connected");
    ui.connectBtn.disabled = true;
    ui.disconnectBtn.disabled = false;
    ui.sendBtn.disabled = false;
    ui.endBtn.disabled = false;
    append(ui, `🔗 connected to ${endpoint}`);
  };

  es.onmessage = (e) => {
    const id = (e as MessageEvent).lastEventId || "-";
    append(ui, `[id ${id}] ${e.data}`);
    // also consume control-plane events in debug mode
    try {
      const payload = JSON.parse((e as MessageEvent).data || "{}");
      if (payload?.type === "tts_ready") {
        const langEvent = String(payload?.lang || "en").toLowerCase();
        if (langEvent) langsWithAudio.add(langEvent);
        updateAudioChipVisibility();
        if (ttsUI.autoTTSEnabled) {
          if (langEvent !== selectedLanguage) return;
          const pid = Number(payload?.pair_id);
          const key = `${langEvent}:${pid}`;
          if (!spokenKeys.has(key)) {
            void ttsService.unlockAudio();
            const rawUrl = String(payload?.url || "");
            const isAbs = /^https?:\/\//i.test(rawUrl);
            let url = isAbs ? rawUrl : location.origin + rawUrl;
            // Add extra tts/ prefix if URL starts with /tts/ but not /tts/tts/
            if (rawUrl.includes("/tts/") && !rawUrl.includes("/tts/tts/")) {
              if (isAbs) {
                // For absolute URLs, insert /tts/ after the domain
                const urlObj = new URL(rawUrl);
                urlObj.pathname = "/tts" + urlObj.pathname;
                url = urlObj.toString();
              } else {
                // For relative URLs, add /tts/ prefix
                url = location.origin + "/tts" + rawUrl;
              }
            }
            console.log("🎵 TTS URL transformation:", {
              rawUrl,
              finalUrl: url,
            });
            ttsService.enqueueUrl(url);
            if (Number.isFinite(pid)) spokenKeys.add(key);
          }
        }
      }
    } catch {}
  };

  es.addEventListener("end", (e: MessageEvent) => {
    append(ui, `[end] ${e.data || "(no body)"}`);
    setStatus(ui, "ended by server");
    try {
      es?.close();
    } catch {}
    es = null;

    ui.connectBtn.disabled = false;
    ui.disconnectBtn.disabled = true;
    ui.sendBtn.disabled = true;
    ui.endBtn.disabled = true;
  });

  es.onerror = () => {
    append(ui, "⚠️ connection problem (retrying…)\n");
    setStatus(ui, "reconnecting…");
  };
}

function disconnect(ui: UI, opts: { silent?: boolean } = {}) {
  if (es) {
    es.close();
    es = null;
  }
  ui.connectBtn.disabled = false;
  ui.disconnectBtn.disabled = true;
  ui.sendBtn.disabled = true;
  ui.endBtn.disabled = true;
  if (!opts.silent) {
    setStatus(ui, "disconnected");
    append(ui, "🔌 disconnected\n");
  }
}

async function sendLine(ui: UI) {
  const sessionId = ui.sessionInput.value.trim();
  const raw = ui.lineInput.value.trim();
  const defaultPayload = { text: "salaam from browser", t: Date.now() };

  let line: unknown = defaultPayload;
  try {
    if (raw) line = JSON.parse(raw);
  } catch {
    line = { text: raw };
  }

  const res = await fetch("/api/publish", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ session_id: sessionId, line }),
  });
  append(ui, `➡️ POST /api/publish → ${res.status}`);
}

async function endSession(ui: UI) {
  const sessionId = ui.sessionInput.value.trim();
  const res = await fetch("/api/publish", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ session_id: sessionId, type: "end" }),
  });
  append(ui, `⛔ end session → ${res.status}`);
}

function setStatus(ui: UI, text: string) {
  ui.statusEl.textContent = text;
}
function append(ui: UI, line: string) {
  ui.logEl.textContent += (ui.logEl.textContent ? "\n" : "") + line;
  (ui.logEl as HTMLElement).scrollTop = ui.logEl.scrollHeight;
}
function q<T extends Element>(sel: string): T {
  const el = document.querySelector(sel);
  if (!el) throw new Error(`Missing element: ${sel}`);
  return el as T;
}
function getSlugFromPath(): string | null {
  // Supports /m/{slug} or /M/{slug} and optional trailing slash
  const m = location.pathname.match(/^\/[mM]\/([^\/?#]+)\/?$/);
  return m ? decodeURIComponent(m[1]) : null;
}

/* ============================================================
   Optional globe demo (kept as-is)
============================================================ */
async function startGlobeDemo() {
  const loadingPlaceholderEl = document.querySelector(
    ".loading-placeholder"
  ) as HTMLElement | null;
  const contentEl = document.querySelector(".content") as HTMLElement | null;
  const globeContainerEl = document.querySelector(
    ".globe-container"
  ) as HTMLElement | null;
  const captionEl = document.querySelector(".caption") as HTMLElement | null;

  let globe: GlobeInstance | undefined;

  try {
    const { latitude: ownLatitude, longitude: ownLongitude } =
      await getOwnCoordinates();

    setTimeout(() => {
      loadingPlaceholderEl?.classList.remove("loading");
      contentEl?.classList.remove("loading");

      if (!globeContainerEl) return;
      globe = initialiseGlobe(globeContainerEl);
      globe.ringsData([
        { lat: Number(ownLatitude), lng: Number(ownLongitude) },
      ]);
      globe.pointOfView({ lat: ownLatitude, lng: ownLongitude });

      globe.onGlobeClick(async ({ lat: endLat, lng: endLng }) => {
        try {
          const distanceBetween = await getDistanceBetween(
            { latitude: Number(ownLatitude), longitude: Number(ownLongitude) },
            { latitude: endLat, longitude: endLng }
          );
          emitArc(globe!, {
            startLat: ownLatitude,
            startLng: ownLongitude,
            endLat,
            endLng,
          });
          if (captionEl)
            captionEl.innerHTML = `the distance between us on the surface of Earth is ${distanceBetween} kilometers`;
        } catch {
          /* ignore */
        }
      });
    }, 800);
  } catch {
    loadingPlaceholderEl?.classList.remove("loading");
    contentEl?.classList.remove("loading");
    if (globeContainerEl) globeContainerEl.style.display = "none";
    if (captionEl) captionEl.textContent = "";
  }
}

/* ============================================================
   Audio Player Integration (from player.html)
============================================================ */
let audioPeerConnection: RTCPeerConnection | null = null;
let audioSessionId: string | null = null;

// Audio UI elements
const audioUI = {
  connectBtn: document.getElementById("audioConnectBtn") as HTMLButtonElement,
  disconnectBtn: document.getElementById(
    "audioDisconnectBtn"
  ) as HTMLButtonElement,
  container: document.getElementById("audio-container") as HTMLElement,
  log: document.getElementById("audioLog") as HTMLElement,
};

function audioLog(message: string) {
  if (!audioUI.log) return;
  audioUI.log.textContent += `[${new Date().toLocaleTimeString()}] ${message}\n`;
  audioUI.log.scrollTop = audioUI.log.scrollHeight;
}

function updateAudioUIState(connected: boolean) {
  if (!audioUI.connectBtn || !audioUI.disconnectBtn) return;

  if (connected) {
    audioUI.connectBtn.style.display = "none";
    audioUI.disconnectBtn.style.display = "inline-block";
  } else {
    audioUI.connectBtn.style.display = "inline-block";
    audioUI.disconnectBtn.style.display = "none";
  }
}

async function connectToAudio() {
  if (!audioUI.connectBtn) return;

  audioUI.connectBtn.disabled = true;
  audioUI.connectBtn.textContent = "Connecting...";

  try {
    await startAudioWebRTCPull();
  } catch (error) {
    audioLog(`❌ Error during connect: ${error}`);
    stopAudioConnection();
  }
}

async function startAudioWebRTCPull() {
  // Use the persistent TTS session ID if available, otherwise fall back to URL slug
  if (persistentTTSSessionId) {
    audioSessionId = persistentTTSSessionId;
    audioLog(
      `🎵 Connecting to persistent TTS session: ${persistentTTSSessionId}`
    );
  } else {
    // Get session ID from URL as fallback
    const pathParts = window.location.pathname.split("/").filter((p) => p);
    const slug = pathParts[1]; // /m/{slug} format
    if (!slug) {
      throw new Error(
        "No session ID found in URL and no TTS session available"
      );
    }
    audioSessionId = slug;
    audioLog(`🎵 Connecting to main session: ${slug}`);
  }

  audioLog("🎵 Starting audio connection...");

  // 1. Create the PeerConnection
  audioPeerConnection = new RTCPeerConnection({
    iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
  });

  // 2. Set up event handlers
  audioPeerConnection.oniceconnectionstatechange = () => {
    audioLog(`🚦 ICE state: ${audioPeerConnection?.iceConnectionState}`);
    if (
      ["connected", "completed"].includes(
        audioPeerConnection?.iceConnectionState || ""
      )
    ) {
      updateAudioUIState(true);
    }
    if (
      ["failed", "disconnected", "closed"].includes(
        audioPeerConnection?.iceConnectionState || ""
      )
    ) {
      audioLog(`❌ Connection lost or failed.`);
      stopAudioConnection();
    }
  };

  // 3. Set up the handler to receive the audio track
  audioPeerConnection.ontrack = (event) => {
    audioLog(`🎵 Received remote audio track.`);
    const mediaElement = document.createElement("audio");
    mediaElement.srcObject = event.streams[0];
    mediaElement.autoplay = true;
    try {
      (mediaElement as any).playsInline = true;
    } catch {}
    mediaElement.controls = true;
    if (audioUI.container) {
      audioUI.container.innerHTML = "";
      audioUI.container.appendChild(mediaElement);
    }
  };

  // 4. Configure the transceiver to receive audio
  audioPeerConnection.addTransceiver("audio", { direction: "recvonly" });

  // 5. Create and set the local description (the "offer")
  const offer = await audioPeerConnection.createOffer();
  await audioPeerConnection.setLocalDescription(offer);

  // 6. Send the offer to the pages function to establish the connection
  const res = await fetch(`/api/audio-connect/${audioSessionId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      sessionDescription: audioPeerConnection.localDescription,
    }),
  });

  if (res.status === 400)
    throw new Error("Audio session has not been published yet.");
  if (!res.ok)
    throw new Error(
      `Failed to connect via worker: ${res.status} ${await res.text()}`
    );

  // 7. Set the remote description (the "answer")
  const answer = await res.json();
  await audioPeerConnection.setRemoteDescription(answer.sessionDescription);
  audioLog("🤝 Audio connection established successfully.");
}

function stopAudioConnection() {
  audioLog("⏹️ Stopping audio connection...");
  if (audioPeerConnection) {
    audioPeerConnection.close();
    audioPeerConnection = null;
  }
  if (audioUI.container) {
    audioUI.container.innerHTML = "";
  }
  updateAudioUIState(false);
  if (audioUI.connectBtn) {
    audioUI.connectBtn.disabled = false;
    audioUI.connectBtn.textContent = "Listen to TTS Audio";
  }
}

// Initialize audio controls
function initAudioControls() {
  if (audioUI.connectBtn) {
    audioUI.connectBtn.addEventListener("click", connectToAudio);
  }
  if (audioUI.disconnectBtn) {
    audioUI.disconnectBtn.addEventListener("click", stopAudioConnection);
  }
}

/* ============================================================
   TTS Service Integration
============================================================ */

// TTS UI elements
const ttsUI = {
  toggleBtn: document.getElementById("ttsToggleBtn") as HTMLButtonElement,
  autoTTSEnabled: false, // Default to disabled
};

function updateTTSToggleState(enabled: boolean) {
  if (!ttsUI.toggleBtn) return;

  // keep chip label constant; reflect pressed state via aria only
  try {
    ttsUI.toggleBtn.setAttribute("aria-pressed", enabled ? "true" : "false");
  } catch {}
}

function toggleTTS() {
  ttsUI.autoTTSEnabled = !ttsUI.autoTTSEnabled;
  updateTTSToggleState(ttsUI.autoTTSEnabled);

  if (ttsUI.autoTTSEnabled) {
    console.log("🎤 TTS enabled - translations will be spoken automatically");
    // anchor at the latest completed pair when user joins
    const completed = Array.from(transcriptPairs.values())
      .filter((p) => !!p.ar && !!p.en)
      .sort((a, b) => a.id - b.id);
    joinAnchorPairId =
      completed.length > 0 ? completed[completed.length - 1].id : null;
    // manifest-driven backfill
    void preRollManifestTTSAudio(3);
  } else {
    console.log("🔇 TTS disabled - translations will not be spoken");
    ttsService.cleanup(); // Stop any currently playing audio
  }
}

// Track the persistent TTS session
let persistentTTSSessionId: string | null = null;

// (client-side generation removed; playback is server/R2-driven)

// Manifest-driven backfill: queue last N urls from R2 manifest
async function preRollManifestTTSAudio(limit: number = 3) {
  if (!ttsUI.autoTTSEnabled) return;
  const sid = currentManifestSessionId;
  if (!sid) return;
  // If Arabic selected, skip entirely
  if (selectedLanguage === "ar") return;
  try {
    const base = location.origin.replace(/\/$/, "");
    const url = `${base}/tts/tts/${encodeURIComponent(sid)}/manifest.json`;
    const res = await fetch(url, {
      headers: { accept: "application/json" },
      cache: "no-store",
    });
    if (!res.ok) return;
    const arr = (await res.json()) as Array<{
      pair_id: number;
      url: string;
      lang?: string;
      ts?: number;
    }>;
    if (!Array.isArray(arr) || arr.length === 0) return;
    // Filter to selected language first, then take the last N of that filtered list.
    const byLang = arr.filter(
      (e) => String(e.lang || "en").toLowerCase() === selectedLanguage
    );
    if (byLang.length === 0) return;
    // Robust ordering: prefer timestamp, fall back to pair_id
    byLang.sort(
      (a, b) => (a.ts ?? 0) - (b.ts ?? 0) || (a.pair_id ?? 0) - (b.pair_id ?? 0)
    );
    const recent = byLang.slice(-limit);

    // If manifest has entries for the selected language, mark it as ready
    if (recent.length > 0) {
      langsWithAudio.add(selectedLanguage);
      updateAudioChipVisibility();
    }
    for (const it of recent) {
      const pid = Number(it.pair_id);
      const lang = String(it.lang || "en").toLowerCase();
      const key = `${lang}:${pid}`;
      if (spokenKeys.has(key)) continue;
      const raw = String(it.url || "");
      const abs = /^https?:\/\//i.test(raw) ? raw : base + raw;
      // Only enqueue for current selected language (non-AR)
      if (selectedLanguage !== "ar") {
        // Apply the same URL transformation logic for manifest URLs
        let finalUrl = abs;
        if (abs.includes("/tts/") && !abs.includes("/tts/tts/")) {
          const urlObj = new URL(abs);
          urlObj.pathname = "/tts" + urlObj.pathname;
          finalUrl = urlObj.toString();
        }
        console.log("🎵 Manifest TTS URL transformation:", {
          raw,
          abs,
          finalUrl,
        });
        ttsService.enqueueUrl(finalUrl);
        if (Number.isFinite(pid)) spokenKeys.add(key);
      }
    }
  } catch (e) {
    console.warn("manifest backfill failed", e);
  }
}

// Initialize TTS controls
function initTTSControls() {
  // bind robustly: prefer a single pointerup to avoid double fire; debounce
  const btn = ttsUI.toggleBtn as HTMLButtonElement | null;
  if (btn) {
    let lastToggleAt = 0;
    const MIN_TOGGLE_INTERVAL_MS = 350;
    const onGesture = () => {
      const now = Date.now();
      if (now - lastToggleAt < MIN_TOGGLE_INTERVAL_MS) return;
      // If button is disabled (no audio yet), ignore
      if ((btn as any).disabled) return;
      lastToggleAt = now;
      void ttsService.unlockAudio();
      toggleTTS();
    };
    if ("onpointerup" in window) {
      btn.addEventListener("pointerup", onGesture as any, { passive: true });
    } else {
      btn.addEventListener("click", onGesture, { passive: true });
    }
  }

  // Hard audio button: always clickable, purely toggles tts without dependencies
  try {
    const hard = document.getElementById(
      "hardAudioBtn"
    ) as HTMLButtonElement | null;
    if (hard) {
      const setState = (on: boolean) => {
        hard.textContent = on ? "Audio ON" : "Audio OFF";
        hard.style.background = on ? "#0b421a" : "#b00020";
        try {
          hard.setAttribute("aria-pressed", on ? "true" : "false");
        } catch {}
      };
      setState(ttsUI.autoTTSEnabled);
      hard.addEventListener("click", () => {
        void ttsService.unlockAudio();
        toggleTTS();
        setState(ttsUI.autoTTSEnabled);
      });
    }
  } catch {}

  // wire hidden audio element for iOS inline playback
  try {
    const hiddenAudio = document.getElementById(
      "ttsAudio"
    ) as HTMLAudioElement | null;
    if (hiddenAudio) {
      hiddenAudio.setAttribute("playsinline", "");
      hiddenAudio.setAttribute("preload", "auto");
      ttsService.setAudioElement(hiddenAudio);
    }
  } catch {}

  // Initialize button state
  updateTTSToggleState(ttsUI.autoTTSEnabled);
  updateAudioChipVisibility();
}

// Initialize language controls
function initLanguageControls() {
  // Get all language toggle buttons
  const langButtons = document.querySelectorAll(
    ".chip--lang"
  ) as NodeListOf<HTMLButtonElement>;

  // Set initial state - English is selected by default
  updateLanguageButtonStates();
  updateTranscriptDisplay();

  // Add click handlers to all language buttons
  langButtons.forEach((btn) => {
    btn.addEventListener("click", () => {
      const lang = btn.dataset.lang;
      if (lang && supportedLanguages.includes(lang)) {
        selectedLanguage = lang;
        updateLanguageButtonStates();
        updateTranscriptDisplay();
        console.log(`Language switched to: ${lang}`);
        try {
          if (
            window.autoscrollManager &&
            typeof window.autoscrollManager.exitReadingMode === "function"
          ) {
            window.autoscrollManager.exitReadingMode();
          }
        } catch {}
        // After language is set and UI updated, reveal transcript and hide grid
        try {
          const grid = document.getElementById(
            "languageGrid"
          ) as HTMLElement | null;
          const transcript = document.getElementById(
            "transcript"
          ) as HTMLElement | null;
          if (grid) grid.style.display = "none";
          if (transcript) transcript.style.display = "block";
        } catch {}
      }
    });
  });
}

// Update the pressed state of language buttons
function updateLanguageButtonStates() {
  const langButtons = document.querySelectorAll(
    ".chip--lang"
  ) as NodeListOf<HTMLButtonElement>;

  langButtons.forEach((btn) => {
    const lang = btn.dataset.lang;
    if (lang === selectedLanguage) {
      btn.setAttribute("aria-pressed", "true");
    } else {
      btn.setAttribute("aria-pressed", "false");
    }
  });
  // Update audio chip visibility when language changes
  updateAudioChipVisibility();
}

// Update transcript display based on selected language
function updateTranscriptDisplay() {
  const pairs = Array.from(transcriptPairs.values());

  pairs.forEach((pair) => {
    const els = pairElements.get(pair.id);
    if (!els) return;

    if (selectedLanguage === "ar") {
      // Deaf mode - show only Arabic
      els.arEl.style.display = "";
      els.enEl.style.display = "none";
    } else {
      // Non-deaf: If translation for selected language exists, show it and hide Arabic; otherwise show Arabic.
      const t =
        pair.translations?.[selectedLanguage] ||
        (selectedLanguage === "en" ? pair.en : undefined);
      if (t) {
        applyTranslationDirection(els.enEl, selectedLanguage);
        els.enEl.textContent = t;
        els.enEl.style.display = "";
        els.arEl.style.display = "none";
      } else {
        els.arEl.style.display = "";
        els.enEl.style.display = "none";
      }
    }
  });
}

// Language management
let selectedLanguage = "en"; // Default to English
const supportedLanguages = [
  "ar", // Arabic only (deaf mode)
  "en", // English
  "ur", // Urdu
  "ms", // Malay
  "bn", // Bengali
  "tr", // Turkish
  "fr", // French
  "es", // Spanish
  "id", // Indonesian
  "hi", // Hindi
]; // Supported language codes

// Helper: set direction and alignment for translation element based on lang code
function applyTranslationDirection(el: HTMLElement, langCode: string) {
  const rtlLangs = new Set(["ar", "ur", "fa", "he", "ps"]);
  if (rtlLangs.has(langCode)) {
    try {
      el.setAttribute("dir", "rtl");
    } catch {}
    el.style.textAlign = "right";
  } else {
    try {
      el.setAttribute("dir", "ltr");
    } catch {}
    el.style.textAlign = "left";
  }
}

// Show/Hide audio chip based on selected language
function updateAudioChipVisibility() {
  const btn = document.getElementById(
    "ttsToggleBtn"
  ) as HTMLButtonElement | null;
  if (!btn) return;

  if (selectedLanguage === "ar") {
    btn.style.display = "none";
    try {
      btn.setAttribute("aria-disabled", "true");
    } catch {}
    try {
      (btn as any).disabled = true;
    } catch {}
  } else {
    btn.style.display = "";
    // Enable only after first tts_ready for the selected language
    const ready = langsWithAudio.has(selectedLanguage);

    // Update button text based on language
    const languageLabels: Record<string, string> = {
      en: "Listen in English",
      ur: "اردو میں سنیں",
      ms: "Dengar dalam Bahasa Melayu",
      bn: "বাংলায় শুনুন",
      tr: "Türkçe dinle",
      fr: "Écouter en français",
      es: "Escuchar en español",
      id: "Dengarkan dalam Bahasa Indonesia",
      hi: "हिंदी में सुनें",
    };

    btn.textContent = languageLabels[selectedLanguage] || "Audio";

    try {
      if (ready) {
        btn.removeAttribute("aria-disabled");
        btn.title = "Toggle audio playback";
      } else {
        btn.setAttribute("aria-disabled", "true");
        btn.title = `Audio will be available when ${selectedLanguage.toUpperCase()} begins`;
      }
    } catch {}
    try {
      (btn as any).disabled = !ready;
    } catch {}
  }
}
